import { io, Socket } from "socket.io-client";
import { sgRealtimeIdToken } from "./realtimeAuth";

const SOCKET_URL =
  (import.meta.env.VITE_SG_REALTIME_SOCKET_URL as string | undefined) ||
  "https://sg-realtime-socket-oyni7keu3a-uc.a.run.app";

let socket: Socket | null = null;

export function getSocket(): Socket {
  if (!socket) {
    socket = io(SOCKET_URL, {
      transports: ["websocket"],
      // sg-realtime exige en el handshake el ID token con canUseSG, igual que
      // /reporting (realtimeAuth.ts). Como función: cada reconexión pide uno
      // vigente (dura 1 h); sin sesión se conecta sin token y el server decide.
      auth: (cb) => {
        sgRealtimeIdToken().then((token) => cb(token ? { token } : {}), () => cb({}));
      },
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });
  }
  return socket;
}

export function connectSocket(): Socket {
  const s = getSocket();
  if (!s.connected) s.connect();
  return s;
}

export function disconnectSocket(): void {
  if (socket?.connected) socket.disconnect();
}

export function subscribeToImeis(imeis: string[]): void {
  if (!imeis?.length) return;
  getSocket().emit("subscribe", { imeis });
}

const CHUNK_SIZE = 200;
const CHUNK_DELAY = 50;

export function getLastPositionsBatch(
  imeis: string[],
  requestId = "tabla"
): void {
  const s = getSocket();
  if (!s.connected) {
    console.warn("[sg-socket] getLastPositionsBatch: socket no conectado");
    return;
  }
  for (let i = 0; i < imeis.length; i += CHUNK_SIZE) {
    const chunk = imeis.slice(i, i + CHUNK_SIZE);
    const delay = (i / CHUNK_SIZE) * CHUNK_DELAY;
    const emit = () => {
      if (s.connected) s.emit("get:last_positions", { imeis: chunk, requestId });
    };
    if (delay === 0) emit();
    else setTimeout(emit, delay);
  }
}

export function fetchLastPositions(
  imeis: string[],
  {
    requestId = "export",
    idleTimeout = 8000,
  }: { requestId?: string; idleTimeout?: number } = {}
): Promise<Map<string, any>> {
  return new Promise((resolve) => {
    if (!imeis?.length) return resolve(new Map());
    const s = connectSocket();
    const pending = new Set(imeis);
    const result = new Map<string, any>();
    let timer: ReturnType<typeof setTimeout>;

    const finish = () => {
      s.off("last_positions", onBatch);
      clearTimeout(timer);
      resolve(result);
    };
    const arm = () => {
      clearTimeout(timer);
      timer = setTimeout(finish, idleTimeout);
    };
    const onBatch = ({ items, requestId: rid }: any = {}) => {
      if (rid && rid !== requestId) return;
      for (const it of items || []) {
        if (!it?.imei) continue;
        result.set(it.imei, it);
        pending.delete(it.imei);
      }
      if (pending.size === 0) finish();
      else arm();
    };
    const send = () => {
      for (let i = 0; i < imeis.length; i += CHUNK_SIZE) {
        s.emit("get:last_positions", {
          imeis: imeis.slice(i, i + CHUNK_SIZE),
          requestId,
        });
      }
      arm();
    };

    s.on("last_positions", onBatch);
    if (s.connected) send();
    else s.once("connect", send);
  });
}
